/**
 * Lease Capabilities
 *
 * Derives UI capabilities from the two-dimensional lease model:
 *   Dimension 1: Lease source (BYO/manual vs wizard/system-managed)
 *   Dimension 2: Payment configuration (enabled vs disabled)
 *
 * Canonical behavior matrix: docs/domains/lease-lifecycle/prd.md § Behavior Matrix
 */

import type { LeaseState, LeaseSource } from '../types/lease';

type PaymentMethodSelection = 'ach' | 'external' | 'none';

export interface LeaseCapabilities {
  /** Lease is in an editable state (active or scheduled) */
  canEdit: boolean;
  /** Lease was created manually (BYO), not via the wizard */
  isByo: boolean;
  /** Inline editing of financial fields (rent, deposit) */
  canEditTerms: boolean;
  /** Inline editing of start/end dates */
  canEditDates: boolean;
  /** Add/remove tenants on the lease */
  canEditTenants: boolean;
  /** Show the Transactions tab */
  showTransactions: boolean;
  /** Show the lease document generation section */
  showDocuments: boolean;
  /** Show recurring charges configuration */
  showCharges: boolean;
  /** Show payment terms and late fee configuration */
  showPaymentTerms: boolean;
  /** Payments are fully disabled (no ACH, no external) */
  paymentsDisabled: boolean;
  /**
   * Schedule (or re-schedule) a move-out (BR-LL-08). True for 'active' | 'ending' —
   * re-scheduling an already-ending lease is allowed. A move-out is NOT a lease-terms
   * edit, so this is intentionally NOT gated on canEdit/isByo (available for both
   * BYO and wizard-sourced leases).
   */
  canScheduleMoveOut: boolean;
  /**
   * Set a per-tenant departure date (BR-LL-06). True for 'active' | 'scheduled' | 'ending' —
   * an ending lease is still tenanted up to its move-out date, so recording an individual
   * tenant's departure remains valid. Like `canScheduleMoveOut`, this is intentionally NOT
   * gated on `canEdit`/`isByo`: a per-tenant departure is not a lease-terms edit, so it must
   * stay reachable once `ending` becomes the common state for a fixed-term lease with a
   * future end date.
   */
  canSetTenantDeparture: boolean;
  /**
   * Post a manual ledger entry (charge/credit). FALSE only when 'archived' (BR-LL-11 —
   * archive is read-only). TRUE for 'ended' — settlement continues past tenancy end
   * (BR-LL-10), so manual charges and deposit disposition remain available.
   */
  canPostLedgerEntry: boolean;
}

export function getLeaseCapabilities(
  state: LeaseState | undefined,
  source: LeaseSource | undefined,
  paymentMethod: PaymentMethodSelection,
): LeaseCapabilities {
  const canEdit = state === 'active' || state === 'scheduled';
  const isByo = source === 'manual';
  const paymentsDisabled = paymentMethod === 'none';
  const canScheduleMoveOut = state === 'active' || state === 'ending';
  const canSetTenantDeparture = state === 'active' || state === 'scheduled' || state === 'ending';
  const canPostLedgerEntry = state !== 'archived';

  return {
    canEdit,
    isByo,
    canEditTerms: canEdit && isByo,
    canEditDates: canEdit && isByo,
    canEditTenants: canEdit && isByo,
    showTransactions: !paymentsDisabled,
    showDocuments: !isByo,
    showCharges: true, // always visible — advisory banner when payments disabled
    showPaymentTerms: !paymentsDisabled,
    paymentsDisabled,
    canScheduleMoveOut,
    canSetTenantDeparture,
    canPostLedgerEntry,
  };
}
